import {
  MMMDataQAComment,
  MMMDataQACommentThread,
  MMMDataQAResolvedThread,
  MMMDataQAThreadScope
} from './data-qa-types';

// Comments per tab, for the step bar's markers: each tab counts the open threads of every
// model whose thread it shows (modelKeysByTab; see tabThreadOwner). A tab split by stage
// counts both stages.
export const commentCountsByTab = (
  threads: MMMDataQACommentThread[],
  modelKeysByTab: Record<string, string[]>
): Record<string, number> =>
  threads
    .filter(t => modelKeysByTab[t.tab]?.includes(t.modelKey))
    .reduce<Record<string, number>>(
      (counts, t) => ({ ...counts, [t.tab]: (counts[t.tab] ?? 0) + t.comments.length }),
      {}
    );

// One model's data, for deciding whether a tab's thread is shared with other models.
export interface ModelSectionHashes {
  // The model's primary config path, as in a thread's modelKey.
  modelKey: string;
  // "Commerce MMM", for "Shared with Commerce MMM".
  label: string;
  // Each stage's section content hashes, or undefined until it has loaded.
  stageHashes: (Record<string, string> | undefined)[];
}

// Whose thread a tab uses, and why.
export interface TabThreadOwner {
  modelKey: string;
  // Every model whose data on the tab matches the active model's, the active one included,
  // in model order. The tab shows all their open threads together, and their resolved
  // threads make up its history.
  modelKeys: string[];
  // The other models sharing the thread.
  sharedWithLabels: string[];
}

// A model's data on a tab as one key, or undefined where it can't be shared (yet): a stage
// hasn't loaded (or failed to), a hash is missing, or its stages differ on the tab (each
// stage has its own thread).
const tabDataKey = (model: ModelSectionHashes, sections: string[]): string | undefined => {
  const keys = model.stageHashes.map(hashes =>
    hashes && sections.every(section => hashes[section])
      ? sections.map(section => hashes[section]).join('|')
      : undefined
  );
  if (keys.some(key => key === undefined) || new Set(keys).size !== 1) return undefined;
  return keys[0];
};

// Least reviews: where a tab's data is identical across models (every section on it has
// the same content hash), they share one thread instead of discussing the same data
// separately. The thread is a model's own, so it survives data refreshes: whichever
// sharing model already has an open thread on the tab, else the first in model order. If
// the data later differs, each model goes back to its own thread. A model whose data hasn't
// loaded doesn't share yet; it joins once it has (see mergeOpenThreads for the threads
// that meet then).
export const tabThreadOwner = (
  models: ModelSectionHashes[],
  activeModelKey: string,
  tab: string,
  sections: string[],
  openThreads: MMMDataQACommentThread[]
): TabThreadOwner => {
  const own: TabThreadOwner = {
    modelKey: activeModelKey,
    modelKeys: [activeModelKey],
    sharedWithLabels: []
  };
  const active = models.find(m => m.modelKey === activeModelKey);
  if (!active || sections.length === 0) return own;
  const activeKey = tabDataKey(active, sections);
  if (activeKey === undefined) return own;

  const sharing = models.filter(
    m => m.modelKey === activeModelKey || tabDataKey(m, sections) === activeKey
  );
  // `sharing` always includes the active model.
  const owner =
    sharing.find(m =>
      openThreads.some(t => t.modelKey === m.modelKey && t.tab === tab && t.stage === '')
    ) ?? sharing[0];
  return {
    modelKey: owner.modelKey,
    modelKeys: sharing.map(m => m.modelKey),
    sharedWithLabels: sharing.filter(m => m.modelKey !== activeModelKey).map(m => m.label)
  };
};

// The open threads a tab shows: its own scope's, plus, where its data is shared, the other
// sharing models' threads on the tab. Models that each had a thread before they started
// sharing (or that posted while their data was still loading) can have one each.
export const openThreadsForScope = (
  threads: MMMDataQACommentThread[],
  { tab, stage }: MMMDataQAThreadScope,
  modelKeys: string[]
): MMMDataQACommentThread[] =>
  threads.filter(t => t.tab === tab && t.stage === stage && modelKeys.includes(t.modelKey));

// Shows several open threads as one conversation, oldest comment first, under the owner's
// thread (where new comments go) when it has one. Resolving it resolves them all.
export const mergeOpenThreads = (
  threads: MMMDataQACommentThread[],
  ownerModelKey: string
): MMMDataQACommentThread | undefined => {
  if (threads.length <= 1) return threads[0];
  const base = threads.find(t => t.modelKey === ownerModelKey) ?? threads[0];
  const comments = threads
    .flatMap(t => t.comments)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id - b.id);
  return { ...base, comments };
};

export const authorLabel = (comment: MMMDataQAComment): string =>
  comment.authorName || comment.authorEmail;

export const isOwnComment = (comment: MMMDataQAComment, userEmail: string): boolean =>
  comment.authorEmail.toLowerCase() === userEmail.toLowerCase();

// Who resolved a thread: "You", else their name, else their email.
export const resolverLabel = (thread: MMMDataQAResolvedThread, userEmail: string): string => {
  if (thread.resolvedByEmail.toLowerCase() === userEmail.toLowerCase()) return 'You';
  return thread.resolvedByName || thread.resolvedByEmail;
};

const timeFormat = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
const dayFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const fullDateFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
});

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// In the viewer's own time zone, like a chat app: "2:14 PM" today, "Yesterday, 2:14 PM",
// "Oct 12, 2:14 PM" this year, "Oct 12, 2025" before that.
export const formatCommentTime = (iso: string, now: Date = new Date()): string => {
  const date = new Date(iso);
  const time = timeFormat.format(date);
  if (sameDay(date, now)) return time;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return `Yesterday, ${time}`;
  if (date.getFullYear() === now.getFullYear()) return `${dayFormat.format(date)}, ${time}`;
  return fullDateFormat.format(date);
};

// Other people's comments posted after the last one this viewer has seen in the thread.
export const unreadCount = (
  thread: MMMDataQACommentThread | undefined,
  lastSeenId: number,
  userEmail: string
): number =>
  thread?.comments.filter(c => c.id > lastSeenId && !isOwnComment(c, userEmail)).length ?? 0;

export const latestCommentId = (thread: MMMDataQACommentThread | undefined): number =>
  thread?.comments.reduce((max, c) => Math.max(max, c.id), 0) ?? 0;

// --- Optimistic updates: a comment shows as soon as it's sent ---------------------------

// A comment still on its way to the server has a temporary negative id.
export const isPendingComment = (comment: MMMDataQAComment): boolean => comment.id < 0;

// Adds a pending comment to the scope's open thread, or to a new temporary thread (sharing
// the comment's id) when the scope has none yet.
export const withPendingComment = (
  threads: MMMDataQACommentThread[],
  scope: MMMDataQAThreadScope,
  pending: MMMDataQAComment
): MMMDataQACommentThread[] => {
  const matches = (t: MMMDataQACommentThread) =>
    t.modelKey === scope.modelKey && t.tab === scope.tab && t.stage === scope.stage;
  if (!threads.some(matches)) {
    return [
      ...threads,
      { ...scope, id: pending.id, createdAt: pending.createdAt, comments: [pending] }
    ];
  }
  return threads.map(t =>
    matches(t) ? { ...t, comments: [...t.comments, { ...pending, threadId: t.id }] } : t
  );
};

// Swaps a pending comment for the server's copy (its temporary thread too, if it started
// one), or with `saved` undefined, takes it back out after a failed post.
export const settlePendingComment = (
  threads: MMMDataQACommentThread[],
  pendingId: number,
  saved?: MMMDataQAComment
): MMMDataQACommentThread[] =>
  threads
    .map(t => {
      if (!t.comments.some(c => c.id === pendingId)) return t;
      const comments = saved
        ? t.comments.map(c => (c.id === pendingId ? saved : c))
        : t.comments.filter(c => c.id !== pendingId);
      return { ...t, id: saved && t.id === pendingId ? saved.threadId : t.id, comments };
    })
    .filter(t => t.comments.length > 0);

// Takes a deleted comment out of its thread right away (a thread left empty goes too, as
// the server leaves it out).
export const withoutComment = (
  threads: MMMDataQACommentThread[],
  commentId: number
): MMMDataQACommentThread[] =>
  threads
    .map(t => ({ ...t, comments: t.comments.filter(c => c.id !== commentId) }))
    .filter(t => t.comments.length > 0);
